import { Injectable } from '@angular/core';
import { IItemsListAction2 } from '@fi-shared/fi-items-list/fi-items-list';
import { AppTextService } from '@fi-shared/app-text';
import { FiOnboardingService } from '../fi-onboarding-builder/fi-onboarding.service';
import {
    IOnboardingUserCompletion,
    IOnboardingUserCompletionBase,
    IOnboardingUserSettingsSaveModel,
} from './fi-onboarding-completion';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiOnboardingReportDataService } from '@fi-app/fi-onboarding/fi-onboarding-report/services/fi-onboarding-report-data.service';
import { FiItemsListOptionType } from '@fi-shared/fi-items-list/fi-items-list-option-type';
import { FiOnboardingHomePageService } from '@fi-app/fi-onboarding/fi-onboarding-home-page/fi-onboarding-home-page.service';
import { FiOnboardingActiveDataService } from '@fi-app/fi-onboarding/fi-onboarding-active/fi-onboarding-active-data.service';
import { IOnboardingActive } from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';

interface IBuildOnboardingCompletionActionsArgs<T extends IOnboardingUserCompletionBase> {
    currentUserCompletion: (item: T) => IOnboardingUserCompletionBase;
    currentOnboardingIds: number[];
}

@Injectable()
export class FiOnboardingCompletionActionsBuilderService {
    constructor(
        private appTextService: AppTextService,
        private fiOnboardingService: FiOnboardingService,
        private fiOnboardingReportDataService: FiOnboardingReportDataService,
        private fiOnboardingHomePageService: FiOnboardingHomePageService,
        private fiOnboardingActiveDataService: FiOnboardingActiveDataService,
    ) {}

    //* onboarding completion actions
    private toggleHomePageVisibilityAction(): IItemsListAction2<IOnboardingUserCompletion> {
        return {
            title: this.appTextService.get('onboarding.show_on_home_page'),
            onClick: (item) => this.setIsHiddenFromHomePage([item.OnboardingId], !item.IsHiddenFromHomePage),
            isChecked: (item) => !item.IsHiddenFromHomePage,
            optionType: FiItemsListOptionType.Checkbox,
            getTooltipText: (item) => this.appTextService.get('onboarding.toggle_home_page_visibility_tooltip'),
            tooltipIconClass: 'far fa-circle-info',
        };
    }

    getMyOnboardingCompletionActions(/*args: { onboardingId: number }*/): IItemsListAction2<IOnboardingUserCompletion>[] {
        return [this.toggleHomePageVisibilityAction(/*args.onboardingId*/), this.getViewReportAction()];
    }

    private getViewReportAction(): IItemsListAction2<IOnboardingUserCompletion> {
        return {
            title: this.appTextService.get('checklist.view_report'),
            condition: (item) => item.HasReportsAccess,
            optionType: FiItemsListOptionType.Icon,
            iconClass: 'far fa-chart-pie',
            onClick: (item) => {
                this.fiOnboardingReportDataService.viewOnboardingReport(item.OnboardingId);
            },
        };
    }

    private getHomePageViewReportAction(args: {
        onboardingId: number;
        hasReportsAccess: boolean;
    }): IItemsListAction2<IOnboardingActive> {
        return {
            title: this.appTextService.get('checklist.view_report'),
            condition: (item) => args.hasReportsAccess,
            /*optionType: args.isIconMode ? FiItemsListOptionType.Icon : FiItemsListOptionType.Common,
            iconClass: 'far fa-chart-pie',*/
            onClick: () => {
                this.fiOnboardingReportDataService.viewOnboardingReport(args.onboardingId);
            },
        };
    }
    //* endregion

    //* region onboarding home page actions

    private getHideFromHomePageAction(onboardingIds: number[]): IItemsListAction2<IOnboardingActive> {
        return {
            title: this.appTextService.get('onboarding.hide_from_home_page'),
            // condition: (item) => !item.IsHiddenFromHomePage,
            onClick: () => this.setIsHiddenFromHomePage(onboardingIds, true),
            getTooltipText: (item) => this.appTextService.get('onboarding.hide_from_home_page_tooltip'),
        };
    }

    private getMoveOnboardingToSideWidgetsAction(onboardingIds: number[]): IItemsListAction2<IOnboardingActive> {
        return {
            title: this.appTextService.get('onboarding.move_to_side_widget'),
            condition: (item) => !this.fiOnboardingHomePageService.isAtSideWidgets(),
            onClick: (item) => {
                this.setIsAtSideWidgets(onboardingIds, true);
            },
        };
    }
    private getMoveOnboardingToFeedAction(onboardingIds: number[]): IItemsListAction2<IOnboardingActive> {
        return {
            title: this.appTextService.get('onboarding.move_to_feed'),
            condition: (item) => this.fiOnboardingHomePageService.isAtSideWidgets(),
            onClick: (item) => {
                this.setIsAtSideWidgets(onboardingIds, false);
            },
        };
    }

    getHomeOnboardingSingleActiveOnboardingActions(
        onboardingActive: IOnboardingActive,
    ): IItemsListAction2<IOnboardingActive>[] {
        return [
            this.getMoveOnboardingToSideWidgetsAction([onboardingActive.Id]),
            this.getMoveOnboardingToFeedAction([onboardingActive.Id]),
            this.getHideFromHomePageAction([onboardingActive.Id]),
            this.getHomePageViewReportAction({
                onboardingId: onboardingActive.Id,
                hasReportsAccess: onboardingActive.HasReportsAccess,
            }),
        ];
    }

    getHomeOnboardingSingleSelectedOnboardingCompletionActions(args: {
        userCompletion: IOnboardingUserCompletion;
    }): IItemsListAction2<IOnboardingActive>[] {
        const onboardingId = args.userCompletion.OnboardingId;
        return [
            this.getHideFromHomePageAction([onboardingId]),
            /*this.getMoveOnboardingToSideWidgetsAction([onboardingId]),
            this.getMoveOnboardingToFeedAction([onboardingId]),*/
            this.getHomePageViewReportAction({
                onboardingId: args.userCompletion.OnboardingId,
                hasReportsAccess: args.userCompletion.HasReportsAccess,
            }),
        ];
    }

    getHomeOnboardingMultipleActiveOnboardingsActions(onboardingIds: number[]): IItemsListAction2<IOnboardingActive>[] {
        return [
            this.getMoveOnboardingToSideWidgetsAction(onboardingIds),
            this.getMoveOnboardingToFeedAction(onboardingIds),
            this.getHideFromHomePageAction(onboardingIds),
        ];
    }

    //* endregion

    //#region onboarding user setting
    subscribeOnUserSettingsChanged(
        ngOnDestroy$: Subject<void>,
        userSettingsChangedFn: (newUserSettings: IOnboardingUserSettingsSaveModel) => void,
    ) {
        this._onboardingUserSettingsChanged$
            .pipe(ngOnDestroy$ ? takeUntil(ngOnDestroy$) : (v) => v)
            .subscribe((newUserSettings) => {
                userSettingsChangedFn(newUserSettings);
            });
    }
    private _onboardingUserSettingsChanged$ = new Subject<IOnboardingUserSettingsSaveModel>();
    private setIsHiddenFromHomePage(onboardingIds: number[], isHiddenFromHomePage: boolean): Promise<void> {
        //return Promise.resolve();

        const userSettings: IOnboardingUserSettingsSaveModel = {
            OnboardingIds: onboardingIds,
            IsHiddenFromHomePage: isHiddenFromHomePage,
        };
        return this.fiOnboardingService.saveOnboardingUserSettings(userSettings).then(() => {
            this.updateOnboardingActivityDataWithUserSettings(userSettings);
            this._onboardingUserSettingsChanged$.next(userSettings);
        });
    }

    private setIsAtSideWidgets(onboardingIds: number[], isAtSideWidgets: boolean) {
        const userSettings: IOnboardingUserSettingsSaveModel = {
            OnboardingIds: onboardingIds,
            IsAtSideWidgets: isAtSideWidgets,
        };
        return this.fiOnboardingService.saveOnboardingUserSettings(userSettings).then(() => {
            this.updateOnboardingActivityDataWithUserSettings(userSettings);
            this._onboardingUserSettingsChanged$.next(userSettings);
        });
    }

    private updateOnboardingActivityDataWithUserSettings(userSettings: IOnboardingUserSettingsSaveModel) {
        this.fiOnboardingActiveDataService.updateOnboardingActivityDataWithUserSettings(userSettings);
    }
    //#endregion
}
